import React from 'react';
import { Row, Col, Form } from 'antd';
import { I18n } from '@aws-amplify/core';

import { Label } from '../../../components/Label';
import { FormItem } from '../../../components/FormItem';
import { SelectWithCreation } from '@/components/ui/Inputs';
import { useCreateSector } from '@/hooks/v1/useSectorMutations';
import type { InputDefaultProps } from '../types';
import type { CreateSectorRequest } from '@/components/ui/Inputs/types/request/sector';

const { useFormInstance, useWatch } = Form;

export function Sector({
	fieldName,
	isLoading = false,
	data = [],
	isError = false
}: Readonly<InputDefaultProps>): JSX.Element {
	const form = useFormInstance();
	const organizationId = useWatch([fieldName, 'organization_id'], form);
	const companyId = useWatch([fieldName, 'company_id'], form);

	const { mutateAsync: createSector, isLoading: isCreatingSector } = useCreateSector();

	function handleSelectSector(id: string): void {
		form.setFieldValue([fieldName, 'sector_id'], id);
		handleClearHierarchy();
	}

	function handleClearSector(): void {
		form.setFieldValue([fieldName, 'sector_id'], undefined);
		handleClearHierarchy();
	}

	function handleClearHierarchy(): void {
		form.setFieldValue([fieldName, 'line_id'], undefined);
		form.setFieldValue([fieldName, 'activity_id'], undefined);
		form.setFieldValue([fieldName, 'workstation_id'], undefined);
	}

	async function handleCreateSector(name: string): Promise<void> {
		const body: CreateSectorRequest = {
			name,
			company_id: companyId,
			company_id_creation: companyId,
			organization_id: organizationId
		};

		const response = await createSector({ payload: body });

		const sectorId = response?.data?.id;

		if (sectorId) {
			handleSelectSector(sectorId);
		}
	}

	const title = I18n.get('Sector');

	return (
		<Col span={24}>
			<Row gutter={[0, 10]}>
				<Col span={24}>
					<Label isRequired>{title}</Label>
				</Col>
				<Col span={24}>
					<FormItem
						name={[fieldName, 'sector_id']}
						rules={[{ required: true, message: I18n.get('Enter the sector') }]}
					>
						<SelectWithCreation
							showSearch
							allowClear
							size="large"
							allowCreation
							placeholder={title}
							loading={isLoading}
							selectOptions={data}
							onClear={handleClearSector}
							onChange={handleSelectSector}
							onCreate={handleCreateSector}
							creatingData={isCreatingSector}
							disabled={isLoading || isError || !companyId}
						/>
					</FormItem>
				</Col>
			</Row>
		</Col>
	);
}
